import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Mail, 
  MessageSquare, 
  Users, 
  Instagram, 
  Send,
  MapPin,
  Phone
} from 'lucide-react';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'membership',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  const contactMethods = [
    {
      title: "Membership Inquiries",
      description: "Join our community of space biology enthusiasts",
      icon: Users,
      email: "pennbioastronautics@gmail.com",
      color: "from-primary-500 to-primary-600"
    },
    {
      title: "Research Collaboration",
      description: "Partner with us on cutting-edge research projects",
      icon: MessageSquare,
      email: "pennbioastronautics@gmail.com",
      color: "from-tertiary-500 to-tertiary-600"
    },
    {
      title: "General Inquiries",
      description: "Questions about our organization or events",
      icon: Mail,
      email: "pennbioastronautics@gmail.com",
      color: "from-secondary-600 to-secondary-700"
    }
  ];

  const socialLinks = [
    {
      name: "Instagram",
      icon: Instagram,
      url: "https://www.instagram.com/upenn_bioastronautics/",
      color: "hover:text-pink-400"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            <span className="cosmic-text">Get</span> In Touch
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl max-w-3xl mx-auto leading-relaxed"
            style={{ color: '#FFF9F0' }}
          >
            Ready to join our mission? Whether you're interested in membership, 
            research collaboration, or partnerships, we'd love to hear from you.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <div className="glass-card p-8">
              <h3 className="text-2xl font-bold mb-6 cosmic-text">Send us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2" style={{ color: '#FFE4BE' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#FFE4BE' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium mb-2" style={{ color: '#FFE4BE' }}>
                    Inquiry Type
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-primary-500 transition-colors duration-200"
                  >
                    <option value="membership">Membership Inquiry</option>
                    <option value="research">Research Collaboration</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="speaking">Speaking Engagement</option>
                    <option value="general">General Question</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2" style={{ color: '#FFE4BE' }}>
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full flex items-center justify-center space-x-2 space-button"
                >
                  <Send className="w-5 h-5" />
                  <span>Send Message</span>
                </motion.button>
              </form>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="space-y-8"
          >
            {/* Contact Methods */}
            <div className="space-y-6">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02, x: 10 }}
                  className="glass-card p-6 group"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${method.color} rounded-lg flex items-center justify-center group-hover:shadow-lg group-hover:shadow-primary-500/25 transition-all duration-300`}>
                      <method.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold mb-2" style={{ color: '#FFE4BE' }}>{method.title}</h4>
                      <p className="text-sm mb-3" style={{ color: '#FFF9F0' }}>{method.description}</p>
                      <a
                        href={`mailto:${method.email}`}
                        className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors duration-200"
                      >
                        {method.email}
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Location & Contact Info */}
            <motion.div
              variants={itemVariants}
              className="glass-card p-6"
            >
              <h4 className="text-lg font-semibold mb-4" style={{ color: '#FFE4BE' }}>Visit Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5" style={{ color: '#FFE4BE' }} />
                  <span style={{ color: '#FFF9F0' }}>
                    University of Pennsylvania<br />
                    School of Engineering and Applied Science<br />
                    Philadelphia, PA 19104
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5" style={{ color: '#FFE4BE' }} />
                  <span style={{ color: '#FFF9F0' }}>(215) 898-8000</span>
                </div>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              variants={itemVariants}
              className="glass-card p-6"
            >
              <h4 className="text-lg font-semibold mb-4" style={{ color: '#FFE4BE' }}>Follow Us</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className={`w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-gray-400 ${social.color} transition-all duration-300 hover:bg-white/10`}
                  >
                    <social.icon className="w-6 h-6" />
                  </motion.a>
                ))}
              </div>
              <p className="text-sm mt-4" style={{ color: '#FFF9F0' }}>
                Stay updated with our latest research, events, and opportunities.
              </p>
            </motion.div>

            {/* Newsletter Signup */}
            <motion.div
              variants={itemVariants}
              className="glass-card p-6"
            >
              <h4 className="text-lg font-semibold mb-4" style={{ color: '#FFE4BE' }}>Newsletter</h4>
              <p className="text-sm mb-4" style={{ color: '#FFF9F0' }}>
                Get monthly updates on our research, events, and opportunities.
              </p>
              <div className="flex space-x-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors duration-200 text-sm"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-4 py-2 bg-gradient-to-r from-primary-500 to-tertiary-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300"
                >
                  Subscribe
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Partnership CTA */}
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mt-16"
        >
          <div className="glass-card p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 cosmic-text">Partnership Opportunities</h3>
            <p className="mb-6" style={{ color: '#FFF9F0' }}>
              We're always looking for industry partners, research institutions, and organizations 
              that share our passion for advancing space biology. Let's collaborate to push the 
              boundaries of human space exploration.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="space-button"
              >
                Explore Partnerships
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 border border-white/20 rounded-lg text-white hover:bg-white/5 transition-all duration-300"
              >
                Download Media Kit
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact; 